import useEventListener from '@use-it/event-listener';
import type { TargetedTouchEvent } from 'preact';
import { useCallback, useMemo, useRef } from 'preact/hooks';

import type { TemplateFunction, TemplateResult } from 'Components/types';
import { cloneSafe } from 'Utils/components';

interface Props {
  template: TemplateFunction<TooltipTemplateParams>;
  name: string;
  field: string;
}

export interface TooltipTemplateParams {
  name: string;
}

export default function Tooltip({ template, name, field }: Props): TemplateResult {
  const rootRef = useRef<HTMLElement>(null);
  const content = useMemo(() => {
    const tipsContainer = document.querySelector('.cm_tooltips-content');
    if (!tipsContainer) {
      return null;
    }
    const elem = tipsContainer.querySelector(`[data-cm-name="${name}"], [data-cm-name="${field}"]`);
    return elem?.innerHTML;
  }, [name, field]);

  useEventListener(
    'touchmove',
    useCallback(
      (e: TouchEvent) =>
        rootRef.current &&
        e.target instanceof Node &&
        !rootRef.current.contains(e.target) &&
        rootRef.current.classList.remove('touched'),
      [],
    ),
  );

  if (!content) {
    return null;
  }

  // Tooltip content is by-design merchant-authored HTML pulled from the
  // page's `.cm_tooltips-content` block. The injection is the documented
  // contract — merchants put rich content (links, formatting) inside
  // tooltips. We do *not* sanitize because doing so would strip features
  // the contract promises. Treat any HTML that ends up in
  // `.cm_tooltips-content` as trusted input, and audit via
  // `document.querySelector('.cm_tooltips-content').innerHTML` if a
  // store reports an XSS in this path.
  const contentElem = (
    <div className="tooltip-content" key="tooltip" dangerouslySetInnerHTML={{ __html: content }}></div>
  );
  const component = template.call({ name });
  return cloneSafe(component, rootRef, { onTouchStart, appendedChildren: contentElem });
}

function onTouchStart({ currentTarget }: TargetedTouchEvent<HTMLElement>) {
  currentTarget.classList.add('touched');
}
